import type { TrainingDocument } from '~/gql/documents/documents'
import { useQuery } from '@apollo/client/react'
import { Download } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import Spinner from '~/components/Spinner'
import { Button } from '~/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '~/components/ui/dialog'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '~/components/ui/table'
import { GQL_DOCUMENT_ACKNOWLEDGEMENTS } from '~/gql/documents/documents'
import { downloadCSV } from '~/utils/exportCsv/utils'
import { acknowledgementsCsv } from './acknowledgements-csv'

type IProps = {
  document: TrainingDocument
  formatDate: (value: unknown) => string
  onClose: () => void
}

/** Qui a pris connaissance d'un document, et quand (#326) : la preuve de remise, exportable. */
function AcknowledgementsDialog({ document, formatDate, onClose }: IProps) {
  const { t } = useTranslation()
  const { data } = useQuery(GQL_DOCUMENT_ACKNOWLEDGEMENTS, { variables: { documentId: document.id }, fetchPolicy: 'network-only' })
  const acks = data?.documentAcknowledgements ?? []

  const exportCsv = () => {
    const { headers, rows } = acknowledgementsCsv(document.title, acks, t, formatDate)
    downloadCSV(`${t('documents.acks.fileName')} - ${document.title}`, headers, rows)
  }

  return (
    <Dialog open onOpenChange={open => !open && onClose()}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{t('documents.acks.title', { title: document.title })}</DialogTitle>
          <DialogDescription>{t('documents.acks.description')}</DialogDescription>
        </DialogHeader>
        {!data
          ? <Spinner className="mx-auto size-6" />
          : acks.length === 0
            ? <p className="text-sm text-muted-foreground">{t('documents.acks.empty')}</p>
            : (
                <>
                  <div className="overflow-x-auto rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('documents.acks.learner')}</TableHead>
                          <TableHead>{t('documents.acks.at')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {acks.map(a => (
                          <TableRow key={`${a.name}-${String(a.at)}`}>
                            <TableCell>
                              <div className="flex flex-col">
                                <span className="font-medium">{a.name}</span>
                                {a.email && <span className="text-xs text-muted-foreground">{a.email}</span>}
                              </div>
                            </TableCell>
                            <TableCell className="whitespace-nowrap">{formatDate(a.at)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <Button variant="outline" className="self-start" onClick={exportCsv}>
                    <Download className="mr-2 size-4" aria-hidden />
                    {t('documents.acks.export')}
                  </Button>
                </>
              )}
      </DialogContent>
    </Dialog>
  )
}

export default AcknowledgementsDialog
